import { initContract } from "@ts-rest/core";
import { z } from "zod";
/**
 * Facility read contract (ADR-004). The tree is returned WHOLE in one response (root + all permitted descendants),
 * not level-by-level — the legacy did root-then-a-request-per-expansion; this replaces it. Derive-on-read: the caller's
 * effective permission per node is computed from their token roles × grants (HPFP-capped), never stored.
 *
 * Browser-safe: zod + ts-rest only (no ORM/Nest/jose). The `-foreground`/entity layers stay server-side.
 */
const c = initContract();
/** The 5-level cascade (ADR-004); order IS the ladder. Mirrors the pg `facility_permission_level` enum. */
export const FACILITY_PERMISSION_LEVELS = [
    "view",
    "manuals_manage",
    "confirm",
    "manage",
    "access_manage",
];
export const FacilityPermissionLevelSchema = z.enum(FACILITY_PERMISSION_LEVELS);
/**
 * One node of the caller's permitted tree. Flat + path-ordered — the client reconstructs the hierarchy from
 * `parentId`/`path` (cheap; each realm's tree is ≤1485 nodes).
 */
export const FacilityNodeSchema = z.object({
    id: z.number().int(),
    parentId: z.number().int().nullable(), // null = a root of the realm's forest
    name: z.string(),
    path: z.string(), // ltree text, e.g. "1.5.23"
    depth: z.number().int().positive(), // nlevel(path)
    archived: z.boolean(),
    /**
     * The caller's effective permission ON this node (HPFP-capped), or **null** when the node is present only as a
     * navigable ancestor of something the caller can reach (descendant-aware view keeps ancestors reachable). `null`
     * ⇒ show/expand it, but the caller has no direct rights here.
     */
    permission: FacilityPermissionLevelSchema.nullable(),
    /**
     * Count of categories attached to this facility (active `facility_category` joins). Populated by the tree reads
     * (`GET /facilities`, `GET /facilities/:id`) to drive the card's "X categories" line; **optional** because
     * node-returning MUTATION responses (move/archive/create) don't compute it (the client refetches the tree).
     */
    categoryCount: z.number().int().optional(),
    /**
     * Count of active manuals attached to this facility (via its active `facility_category` joins). Same
     * tree-read-only population + **optional** semantics as `categoryCount`; drives the card's "K manuals" line (ADR-010).
     */
    manualCount: z.number().int().optional(),
});
export const FacilityTreeSchema = z.object({
    facilities: z.array(FacilityNodeSchema),
});
/**
 * The "what's connected" summary shown before a hard delete (ADR-006 §3). `expected` is an opaque snapshot bound into
 * the delete: the delete re-counts the subtree under the realm lock and 409s if it changed, so a cascade never removes
 * content the user didn't see in the confirmation (panel #5). `manuals` is 0 until the Manuals module.
 */
export const FacilityStatsSchema = z.object({
    // The facility's AUTHORITATIVE current name — the delete dialog's type-to-confirm matches against this, not the
    // client tree's possibly-stale name, and it's bound into `expected` so a concurrent rename 409s the delete (panel).
    name: z.string(),
    descendants: z.number().int(),
    directChildren: z.number().int(),
    archivedDescendants: z.number().int(),
    categories: z.number().int(),
    manuals: z.number().int(),
    // Live linked manuals (ADR-020) in OTHER facilities whose parent is a manual under this subtree — the hard-delete's
    // FK ON DELETE CASCADE removes them too. Surfaced so the confirmation warns before a cross-facility cascade; bound
    // into `expected` so a link created/removed between stats and delete 409s. 0 unless a subtree manual is a link parent.
    linkedChildCount: z.number().int(),
    // Trashed manuals the delete destroys too (ADR-056): those in the subtree, and trashed linked copies outside it. Bound into
    // `expected`. Not part of `manuals` or `linkedChildCount`, which stay live-only.
    trashedManuals: z.number().int(),
    // Opaque content-identity token — a hash of the subtree's facility ids + archived-flags + active category-link ids +
    // ALL manual ids/deleted-flags + external linked-child ids + the root name (panel). The delete recomputes it under the
    // realm lock and 409s if it differs, binding the confirmed CONTENT (not just counts) to the delete, so a concurrent
    // reshuffle / archive / rename / link change can't slip unseen state into the cascade.
    expected: z.string(),
});
const UnauthorizedSchema = z.object({ message: z.string() });
/**
 * A facility id path param, bounded to Postgres int4. Without the upper bound an id above 2^31-1 passes `int()`, reaches
 * the `WHERE id = ?` against the `integer` column, and Postgres raises 22003 → a raw 500. Bounding it here makes an
 * out-of-range id a 400 at the ts-rest boundary. Shared so every future `:id` route reuses the same guard.
 */
export const FacilityIdSchema = z.coerce.number().int().positive().max(2147483647);
export const facilityContract = c.router({
    createFacility: {
        // Create a facility under any parent (ADR-006 §5, RED ZONE). Authz: manage on the parent; root create (parentId
        // null) is bypass-tier only. Rejects an archived parent; auto-grants the creator; attaches the given categories.
        method: "POST",
        path: "/facilities",
        body: z.object({
            name: z.string().trim().min(1).max(255),
            parentId: FacilityIdSchema.nullable(),
            categoryIds: z.array(FacilityIdSchema).default([]),
        }),
        responses: {
            200: FacilityNodeSchema,
            400: z.object({ message: z.string(), code: z.literal("FACILITY_INVALID") }),
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
            409: z.object({ message: z.string(), code: z.literal("FACILITY_ARCHIVED") }),
        },
        summary: "Create a facility under a parent (or a realm root; authz per ADR-006 §5).",
    },
    listFacilities: {
        method: "GET",
        path: "/facilities",
        query: z.object({
            // Consolidates the legacy /facility, /manageFacility, /accessManageFacility lists: return the tree of nodes the
            // caller has >= this level on (the node itself OR a descendant, so the tree stays rooted). Default = view.
            permission: FacilityPermissionLevelSchema.default("view"),
        }),
        responses: {
            200: FacilityTreeSchema,
            401: UnauthorizedSchema,
        },
        summary: "The caller's permitted facility tree for the tenant — whole tree, one response, derive-on-read.",
    },
    getFacility: {
        method: "GET",
        path: "/facilities/:id",
        pathParams: z.object({ id: FacilityIdSchema }),
        responses: {
            200: FacilityNodeSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "One facility if the caller can view it (else 403/404 — opaque, no cross-tenant existence leak).",
    },
    moveFacility: {
        // Reparent a facility (and its whole subtree) within the same tenant — ADR-005 (RED ZONE). The DB rewrites the
        // ltree paths set-based, in one advisory-locked transaction; grants travel with the facility (no recompute).
        // `newParentId: null` moves it to a realm root (bypass-tier only — see ADR-005 §1).
        method: "POST",
        path: "/facilities/:id/move",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({ newParentId: FacilityIdSchema.nullable() }),
        responses: {
            // The affected subtree with fresh paths, path-ordered (the client refetches the tree for now; this lets it
            // patch locally later). Same shape as the list read.
            200: FacilityTreeSchema,
            400: z.object({ message: z.string(), code: z.literal("FACILITY_MOVE_CYCLE") }),
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
            409: z.object({ message: z.string(), code: z.literal("FACILITY_ARCHIVED") }),
        },
        summary: "Move a facility to a new parent within the tenant (ltree subtree rewrite; authz per ADR-005).",
    },
    updateFacility: {
        // Rename a facility (ADR-006 §1). Authz: manage. Not a topology change (no advisory lock).
        method: "PATCH",
        path: "/facilities/:id",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({ name: z.string().trim().min(1).max(255) }),
        responses: {
            200: FacilityNodeSchema,
            400: z.object({ message: z.string(), code: z.literal("FACILITY_INVALID") }),
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "Rename a facility (authz: manage).",
    },
    archiveFacility: {
        // Soft-delete a facility + its whole subtree (ADR-006 §2). Authz: manage on the subtree. Returns the subtree.
        method: "POST",
        path: "/facilities/:id/archive",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({}),
        responses: {
            200: FacilityTreeSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "Archive a facility and its subtree (soft delete; authz: manage on the subtree).",
    },
    unarchiveFacility: {
        method: "POST",
        path: "/facilities/:id/unarchive",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({}),
        responses: {
            200: FacilityTreeSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
            // Two unarchive guards (ADR-006 §2): FACILITY_NOT_ARCHIVED — the target isn't archived (so restoring it would
            // bulk-clear archived DESCENDANTS of an active node); FACILITY_ANCESTOR_ARCHIVED — a strict ancestor is still
            // archived (legacy parity: no active node under an archived parent — restore the parent first).
            409: z.object({
                message: z.string(),
                code: z.enum(["FACILITY_NOT_ARCHIVED", "FACILITY_ANCESTOR_ARCHIVED"]),
            }),
        },
        summary: "Unarchive a facility and its subtree (authz: manage on the subtree; target must be archived, no archived ancestor).",
    },
    facilityStats: {
        // The "what's connected" summary before a hard delete (ADR-006 §3). Authz: manage on the FULL subtree.
        method: "GET",
        path: "/facilities/:id/stats",
        pathParams: z.object({ id: FacilityIdSchema }),
        responses: {
            200: FacilityStatsSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
        },
        summary: "Subtree stats for the delete confirmation (authz: manage on the full subtree).",
    },
    deleteFacility: {
        // Hard delete the subtree (ADR-006 §4, RED ZONE). Authz: manage on the FULL subtree. The `expected*` query params
        // are the snapshot from `facilityStats`; the delete re-counts under the lock and 409s if the subtree changed.
        method: "DELETE",
        path: "/facilities/:id",
        pathParams: z.object({ id: FacilityIdSchema }),
        query: z.object({ expected: z.string().min(1) }),
        responses: {
            200: z.object({ deleted: z.number().int() }),
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
            409: z.object({ message: z.string(), code: z.literal("FACILITY_STATS_STALE") }),
        },
        summary: "Hard-delete a facility subtree after a stats confirmation (authz: manage on the full subtree).",
    },
    duplicateFacility: {
        // Deep-copy a facility subtree (ADR-008, RED ZONE). New ids + regenerated paths; root named "<name> Copy", placed
        // as a sibling; copies attached categories + (issuance-gated) grants; manuals/assets deferred; copy is fully
        // active; archived source rejected. Authz: manage on the FULL subtree AND create-authz on the parent (root =
        // bypass-only). Additive — no stats/TOCTOU token.
        method: "POST",
        path: "/facilities/:id/duplicate",
        pathParams: z.object({ id: FacilityIdSchema }),
        body: z.object({}),
        responses: {
            // The NEW subtree (path-ordered) — same shape as the list read, so the client can drop it in / refetch.
            200: FacilityTreeSchema,
            401: UnauthorizedSchema,
            403: z.object({ message: z.string(), code: z.literal("FACILITY_FORBIDDEN") }),
            404: z.object({ message: z.string(), code: z.literal("FACILITY_NOT_FOUND") }),
            409: z.object({ message: z.string(), code: z.literal("FACILITY_ARCHIVED") }),
        },
        summary: "Duplicate a facility subtree as a sibling (deep copy; authz: manage on the full subtree + create on parent).",
    },
});
//# sourceMappingURL=facility.contract.js.map